import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import QuoteCalculator from "./pages/QuoteCalculator";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import SplashPage from "./pages/SplashPage";
import CategoryPage from "./pages/CategoryPage";
import SubcategoryDetail from "./pages/SubcategoryDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TOTPSetup from "./pages/TOTPSetup";
import TOTPChallenge from "./pages/TOTPChallenge";
import AccountSettings from "./pages/AccountSettings";
import POHistory from "./pages/POHistory";
import ActionQueue from "./pages/AdminDashboard/ActionQueue";
import AdminOrders from "./pages/AdminDashboard/AdminOrders";
import AdminProofing from "./pages/AdminDashboard/AdminProofing";
import AdminProduction from "./pages/AdminDashboard/AdminProduction";
import AdminQuotes from "./pages/AdminDashboard/AdminQuotes";
import AdminCustomers from "./pages/AdminDashboard/AdminCustomers";
import AdminSystem from "./pages/AdminDashboard/AdminSystem";
import AdminPricingImport from "./pages/AdminPricingImport";
import UploadArtwork from "./pages/UploadArtwork";
import OrderSummaryPage from "./pages/OrderSummaryPage";
import CustomerInfoPage from "./pages/CustomerInfoPage";
import ReviewOrderPage from "./pages/ReviewOrderPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import ProofApprovalPage from "./pages/ProofApprovalPage";
import PublicMockupPage from "./pages/PublicMockupPage";
import PricingCatalog from "./pages/PricingCatalog";
import CustomerDashboard from "./pages/CustomerDashboard";
import WelcomePage from "./pages/WelcomePage";
import Crash from "./pages/Crash";
import SeoLandingPage from "./pages/SeoLandingPage";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import SalesResources from "./pages/SalesResources";
import SalesResourceCategory from "./pages/SalesResourceCategory";
import { Seo } from "./components/Seo";
import { LANDING_PAGES } from "./data/seoData";
import CrispChat from "./components/CrispChat";
import AuthCallback from "./pages/AuthCallback";
import { log } from "./lib/logger";
import { AuthProvider } from "./contexts/AuthContext";
import { BreadcrumbProvider } from "./contexts/BreadcrumbContext";
// iter114e: StrategicPartnerBanner inlined into TopNav — its full
// row of vertical real estate freed up on every page, with the
// award/copy preserved as an evergreen-tier-only pill in the nav.
import { ErrorBoundary } from "./components/ErrorBoundary";
import { TopNav } from "./components/TopNav";
import { Toaster } from "./components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import { useLocation } from "react-router-dom";
// iter114s.20 — `FloatingQuoteSummary` was the floating "Your Quote"
// popup that mirrored the wizard total in a sticky card. Customer asked
// to delete it ("Please delete this as it's no longer required.") so we
// removed the import + render below. The component file itself is left
// in the codebase in case a future feature wants to reuse the layout.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      log.info('API connection successful:', response.data);
    } catch (e) {
      log.error('API connection failed:', e);
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  return (
    <>
      <Seo
        title="Instant Quote — Custom Stickers, Labels & Decals | Evergreen Graphics Group"
        description="Get an instant online quote for custom stickers, labels, decals, DTF transfers and more. Any size, shape and quantity — printed in Canada, shipped across Canada & the USA."
        path="/quote"
      />
      <QuoteCalculator />
    </>
  );
};

// Hide site chrome (TopNav, banners, floating quote) on public-facing share
// pages so the recipient sees a clean standalone landing experience.
const CHROMELESS_PATH_PREFIXES = ['/m/'];

const AppChrome = () => {
  const location = useLocation();
  const chromeless = CHROMELESS_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (chromeless) return null;
  return (
    <>
      <TopNav />
      <CrispChat />
    </>
  );
};

// Emergent Google OAuth return leg — the fragment #session_id=... must be
// processed BEFORE any route or auth check runs. Synchronous render-time
// check via useLocation() (NOT useEffect / window.location — not reactive).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthGateway = ({ children }) => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BreadcrumbProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTop />
              <AppChrome />
              <AuthGateway>
              <Routes>
                <Route path="/" element={<SplashPage />} />
                <Route path="/splash" element={<SplashPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/totp-setup" element={<TOTPSetup />} />
                <Route path="/totp-challenge" element={<TOTPChallenge />} />
                <Route path="/account-settings" element={<AccountSettings />} />
                <Route path="/po-history" element={<POHistory />} />
                <Route path="/admin" element={<Navigate to="/admin/action-queue" replace />} />
                <Route path="/admin/action-queue" element={<ActionQueue />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/proofing" element={<AdminProofing />} />
                <Route path="/admin/production" element={<AdminProduction />} />
                <Route path="/admin/quotes" element={<AdminQuotes />} />
                <Route path="/admin/customers" element={<AdminCustomers />} />
                <Route path="/admin/system" element={<AdminSystem />} />
                <Route path="/admin/pricing-import" element={<AdminPricingImport />} />
                <Route path="/quote" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/product/:productId" element={<ProductDetail />} />
                <Route path="/category/:categoryId" element={<CategoryPage />} />
                <Route path="/category/:categoryId/product/:productId" element={<SubcategoryDetail />} />
                <Route path="/upload-artwork" element={<UploadArtwork />} />
                <Route path="/order-summary" element={<OrderSummaryPage />} />
                <Route path="/customer-info" element={<CustomerInfoPage />} />
                <Route path="/review-order" element={<ReviewOrderPage />} />
                <Route path="/review-order/:orderId" element={<ReviewOrderPage />} />
                <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                <Route path="/proof-approval/:orderId" element={<ProofApprovalPage />} />
                <Route path="/m/:token" element={<PublicMockupPage />} />
                <Route path="/pricing-catalog" element={<PricingCatalog />} />
                <Route path="/dashboard" element={<CustomerDashboard />} />
                <Route path="/welcome" element={<WelcomePage />} />
                {/* SEO keyword landing pages — content lives in data/seoData.js */}
                {LANDING_PAGES.map((p) => (
                  <Route key={p.slug} path={`/${p.slug}`} element={<SeoLandingPage slug={p.slug} />} />
                ))}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:blogSlug" element={<BlogPost />} />
                <Route path="/sales-resources" element={<SalesResources />} />
                <Route path="/sales-resources/:categorySlug" element={<SalesResourceCategory />} />
                {process.env.NODE_ENV !== 'production' && (
                  <Route path="/__crash" element={<Crash />} />
                )}
                {/* iter114s.76 — catch-all 404 so /admin/classic and any other
                    typo'd URL renders a real NotFound page instead of an
                    empty body under the splash chrome. */}
                <Route
                  path="*"
                  element={(
                    <div className="min-h-screen bg-gradient-to-br from-[#F4FBF6] via-white to-[#E8F1E6] flex items-center justify-center px-4">
                      <Seo
                        title="Page Not Found | Evergreen Graphics Group"
                        description="The page you were looking for doesn't exist."
                        path="/404"
                        noindex
                      />
                      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-[#E5E7EB] p-8 text-center">
                        <div className="text-6xl font-black text-[#6FBF73] mb-3">404</div>
                        <h1 className="text-2xl font-bold text-[#14281D] mb-2">Page not found</h1>
                        <p className="text-[#1F3D2C]/70 mb-5">
                          We couldn't find that page. The URL may have changed or the page may have been retired.
                        </p>
                        <a href="/splash" className="inline-block bg-[#6FBF73] hover:bg-[#4A7C59] text-white font-semibold px-5 py-2.5 rounded-full transition">
                          ← Back to home
                        </a>
                      </div>
                    </div>
                  )}
                />
              </Routes>
              </AuthGateway>
              {/* Global toast surface — admin dashboard + auth flows
                  use sonner toasts instead of native alert() popups. */}
              <Toaster richColors position="top-right" closeButton />
            </BrowserRouter>
          </div>
        </BreadcrumbProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
